import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../lib/utils';

/**
 * Input — labeled text input with optional description and error state.
 */
const inputVariants = cva(
  [
    'flex w-full bg-bg text-fg placeholder:text-fg-subtle',
    'border rounded-md transition-colors duration-150',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1 focus-visible:ring-offset-bg',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'file:border-0 file:bg-transparent file:text-sm file:font-medium',
  ].join(' '),
  {
    variants: {
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-9 px-3 text-sm',
        lg: 'h-11 px-4 text-base',
      },
      state: {
        default: 'border-border hover:border-border-strong focus-visible:border-brand',
        error: 'border-destructive/50 focus-visible:ring-destructive focus-visible:border-destructive',
      },
    },
    defaultVariants: { size: 'md', state: 'default' },
  }
);

export const Input = React.forwardRef(function Input(
  { className, size, state, type = 'text', ...props },
  ref
) {
  return (
    <input
      ref={ref}
      type={type}
      className={cn(inputVariants({ size, state }), className)}
      {...props}
    />
  );
});

/**
 * Field — labeled input with optional description and error message.
 */
export function Field({
  label,
  description,
  error,
  required = false,
  htmlFor,
  children,
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={htmlFor} className="text-xs font-medium text-fg flex items-center gap-1">
          {label}
          {required && <span className="text-destructive">*</span>}
        </label>
      )}
      {children}
      {description && !error && (
        <p className="text-2xs text-fg-subtle">{description}</p>
      )}
      {error && <p className="text-2xs text-destructive">{error}</p>}
    </div>
  );
}